import React from 'react';
import { Button, Image, Table, Tag } from 'antd';
import moment from 'moment';
import './ProductListTable.css';

function renderStatus(status) {
    return <Tag color={status === 'active' ? 'green' : 'default'}>{status || 'inactive'}</Tag>;
}

function getAttachmentUrl(attachment) {
    if (typeof attachment === 'string') return attachment.trim();
    return `${attachment?.url || ''}`.trim();
}

function getProductImageUrl(product) {
    const attachmentImage = (product.attachments || [])
        .map(getAttachmentUrl)
        .find(Boolean);
    if (attachmentImage) return attachmentImage;
    return (product.variants || []).map((variant) => `${variant.image || ''}`.trim()).find(Boolean) || '';
}

function ImageThumbnail({ src, alt, size = 40 }) {
    if (!src) {
        return (
            <div style={{
                width: size,
                height: size,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#9ca3af',
                background: '#f5f5f5',
                border: '1px solid #e5e7eb',
                borderRadius: 6
            }}>
                —
            </div>
        );
    }

    return (
        <Image
            width={size}
            height={size}
            src={src}
            alt={alt}
            style={{ objectFit: 'cover', borderRadius: 6 }}
        />
    );
}

export default function ProductListTable(props) {
    const { data = [], loading, page, limit, total, onPageChange, onEdit } = props;

    const columns = [
        {
            title: 'Ảnh SP',
            key: 'image',
            width: 56,
            render: (_, item) => (
                <ImageThumbnail
                    src={getProductImageUrl(item)}
                    alt={`Ảnh sản phẩm ${item.title || ''}`}
                />
            )
        },
        {
            title: 'Tên sản phẩm',
            dataIndex: 'title',
            key: 'title',
            width: 300,
            render: (title) => <span className="product-list-table__ellipsis product-list-table__title" title={title}>{title}</span>
        },
        {
            title: 'Danh mục',
            key: 'category',
            width: 120,
            render: (_, item) => {
                const categoryName = item.category ? item.category.name : '';
                return <span className="product-list-table__ellipsis" title={categoryName}>{categoryName}</span>;
            }
        },
        {
            title: 'Trạng thái',
            key: 'status',
            width: 96,
            render: (_, item) => renderStatus(item.status)
        },
        {
            title: 'Variant',
            key: 'variant_count',
            width: 64,
            align: 'center',
            render: (_, item) => <span className="product-list-table__number">{item.variant_count || 0}</span>
        },
        {
            title: 'Khoảng giá',
            key: 'price_range',
            width: 160,
            render: (_, item) => {
                if (!item.has_variants) return <span className="product-list-table__muted">Chưa có giá</span>;
                return (
                    <span className="product-list-table__single-line product-list-table__price">
                        {`${new Intl.NumberFormat('vi-VN').format(item.min_price || 0)}đ - ${new Intl.NumberFormat('vi-VN').format(item.max_price || 0)}đ`}
                    </span>
                );
            }
        },
        {
            title: 'Sẵn sàng',
            key: 'ready_state',
            width: 112,
            render: (_, item) => <span className="product-list-table__single-line">{item.has_variants ? 'Có thể bán' : 'Chưa có variant'}</span>
        },
        {
            title: 'Ngày tạo',
            key: 'created_time',
            width: 136,
            render: (_, item) => <span className="product-list-table__single-line product-list-table__muted">{moment(item.created_time).format('HH:mm DD-MM-YYYY')}</span>
        },
        {
            title: 'Hành động',
            key: 'actions',
            width: 92,
            render: (_, item) => <Button className="product-list-table__edit" size="small" type="primary" onClick={() => onEdit(item)}>Chỉnh sửa</Button>
        }
    ];

    return (
        <Table
            rowKey="_id"
            loading={loading}
            columns={columns}
            dataSource={data}
            className="product-list-table"
            tableLayout="fixed"
            scroll={{ x: 1180 }}
            expandable={{
                columnWidth: 36,
                expandedRowRender: (record) => (
                    <div>
                        {(record.variants || []).length === 0 ? 'Chưa có variant' : (
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Ảnh</th>
                                        <th>SKU</th>
                                        <th>Thuộc tính</th>
                                        <th>Giá</th>
                                        <th>Trạng thái</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(record.variants || []).map((variant) => (
                                        <tr key={variant._id}>
                                            <td>
                                                <ImageThumbnail
                                                    src={`${variant.image || ''}`.trim()}
                                                    alt={`Ảnh biến thể ${variant.sku || variant.name || ''}`}
                                                />
                                            </td>
                                            <td>{variant.sku}</td>
                                            <td>{Object.keys(variant.attributes || {}).map((key) => `${key}: ${variant.attributes[key]}`).join(', ')}</td>
                                            <td>{new Intl.NumberFormat('vi-VN').format(variant.sale_price || 0)}đ</td>
                                            <td>{renderStatus(variant.status)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )
            }}
            pagination={{
                current: page,
                pageSize: limit,
                total,
                onChange: onPageChange,
                showSizeChanger: true,
                onShowSizeChange: onPageChange
            }}
        />
    );
}
